import React, { useMemo, useState } from "react";
import { MessageCircle, Wrench, Search } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import SafeImage from "@/components/SafeImage";
import { INSTRUMENTS, INSTRUMENT_CATEGORIES } from "@/data/instruments";
import { buildWaLink } from "@/lib/whatsapp";
import { BAH_INSTRUMENTS_CATALOGUE_PDF, BAH_INSTRUMENTS_CATALOGUE_FILENAME } from "@/lib/assets";

export default function Instruments() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");

  const grouped = useMemo(() => {
    const filtered = INSTRUMENTS.filter((i) => {
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        i.name.toLowerCase().includes(q) ||
        (i.desc || "").toLowerCase().includes(q) ||
        i.code.toLowerCase().includes(q);
      const matchesCategory = category === "all" || i.category === category;
      return matchesQuery && matchesCategory;
    });
    return INSTRUMENT_CATEGORIES.map((c) => ({
      ...c,
      items: filtered.filter((i) => i.category === c.id),
    })).filter((g) => g.items.length > 0);
  }, [query, category]);

  const total = grouped.reduce((acc, g) => acc + g.items.length, 0);

  return (
    <div data-testid="instruments-page">
      <PageHero
        testId="instruments-hero"
        eyebrow="Veterinary Instruments"
        title="Tools that work in East African field conditions."
        lede="From castration and identification to dosing, hoof care, calving, milking, sheep/goat and small-animal instruments — the BAH Instruments Catalogue 2026."
        image="https://images.pexels.com/photos/6235117/pexels-photo-6235117.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1600"
        primaryCta={{
          label: "Download Instruments Catalogue",
          href: BAH_INSTRUMENTS_CATALOGUE_PDF,
          external: true,
        }}
        secondaryCta={{ label: "WhatsApp Technical Team", href: buildWaLink("Hello Biodeal Animal Health, I'd like a recommendation on veterinary instruments."), external: true }}
      />

      {/* Filter */}
      <section className="sticky top-16 md:top-20 z-40 bg-bah-paper/95 backdrop-blur border-b border-bah-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-bah-mute" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search instruments by name, code or description…"
              className="w-full pl-10 pr-4 py-3 rounded-full border border-bah-line bg-white focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30 text-sm"
              data-testid="instruments-search"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="instruments-filter-category"
            className="rounded-full border border-bah-line bg-white px-4 py-3 text-sm focus:outline-none focus:border-bah-green focus:ring-2 focus:ring-bah-green/30"
          >
            <option value="all">All instrument categories</option>
            {INSTRUMENT_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-bah-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-4 mb-10">
            <div>
              <SectionLabel>Catalogue 2026</SectionLabel>
              <h2 className="mt-3 font-display text-2xl md:text-3xl text-bah-ink">
                {total} instrument{total === 1 ? "" : "s"} matched
              </h2>
            </div>
          </div>

          {grouped.length === 0 && (
            <div className="bg-white border border-bah-line rounded-2xl p-10 text-center">
              <p className="text-bah-ink font-display text-xl">No instruments matched.</p>
              <p className="mt-2 text-bah-mute text-sm">Try clearing the search.</p>
            </div>
          )}

          <div className="space-y-16">
            {grouped.map((g) => (
              <div key={g.id} data-testid={`instrument-group-${g.id}`}>
                <h3 className="font-display text-xl md:text-2xl text-bah-ink border-b border-bah-line pb-3">
                  {g.label}{" "}
                  <span className="text-bah-mute text-sm font-sans font-medium">
                    ({g.items.length})
                  </span>
                </h3>
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {g.items.map((i) => (
                    <article
                      key={i.code}
                      className="card-rise bg-white border border-bah-line rounded-2xl overflow-hidden flex flex-col"
                      data-testid={`instrument-card-${i.code.replace(/\s/g, "-")}`}
                    >
                      {i.image ? (
                        <div className="relative aspect-[4/3] bg-bah-paperAlt overflow-hidden border-b border-bah-line">
                          <SafeImage
                            src={i.image}
                            alt={`${i.name} (${i.code}) — Biodeal Animal Health instruments catalogue page`}
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover object-center"
                          />
                        </div>
                      ) : null}
                      <div className="p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-bah-green font-bold">
                              {i.code}
                            </div>
                            <h4 className="mt-1 font-display text-lg text-bah-ink leading-snug">
                              {i.name}
                            </h4>
                            {i.desc && (
                              <p className="mt-2 text-sm text-bah-mute leading-relaxed">{i.desc}</p>
                            )}
                          </div>
                          <div className="h-9 w-9 shrink-0 rounded-lg bg-bah-yellow/30 grid place-content-center text-bah-ink">
                            <Wrench className="h-4 w-4" />
                          </div>
                        </div>
                        <a
                          href={buildWaLink(`Hello Biodeal Animal Health, I would like to inquire about ${i.name} (${i.code}).`)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-bah-green hover:text-bah-orange"
                          data-testid={`instrument-inquire-${i.code.replace(/\s/g, "-")}`}
                        >
                          <MessageCircle className="h-4 w-4" /> Inquire
                        </a>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
